/**
 * Serialización para el nodo de payment
 */

import type { ApiNode, NodeData, Input } from "@/workflow_api/types/workflow";
import type { NodeVariable } from "@/workflow/components/configurations/types";

// PaymentItem interface
export interface PaymentItem {
  id: string;
  concept: string;
  amount: string;
  conceptType: "input" | "variable";
  amountType: "input" | "variable";
  conceptNode?: string;
  conceptField?: string;
  conceptOutput?: string;
  conceptGlobalType?: {
    value: string;
    type:
      | "APPLICANT"
      | "NODE_EXECUTION_START"
      | "NODE_EXECUTION_FINISH"
      | "CURRENT_TIME";
  };
  amountNode?: string;
  amountField?: string;
  amountOutput?: string;
  amountGlobalType?: {
    value: string;
    type:
      | "APPLICANT"
      | "NODE_EXECUTION_START"
      | "NODE_EXECUTION_FINISH"
      | "CURRENT_TIME";
  };
  hasChange: boolean;
}

interface PaymentData {
  title: string;
  description: string;
  paymentRequired: boolean;
  express: boolean;
  paymentItems: PaymentItem[];
}

/**
 * Helper para obtener el nodeId y fieldId de una variable seleccionada
 */
const getVariableConnection = (
  variableId: string,
  variables?: NodeVariable[],
): {
  nodeId: string;
  fieldId: string | null;
  fieldInfo?: { id: string; name: string; type: string };
} | null => {
  if (!variables) return null;

  for (const nodeVar of variables) {
    const variable = nodeVar.variables.find((v) => {
      const fullId = v.node_id ? `${v.node_id}_${v.id}` : v.id;
      return fullId === variableId;
    });

    if (variable) {
      const nodeId = variable.node_id || nodeVar.nodeId;
      const fieldId = variable.id;

      return {
        nodeId,
        fieldId: fieldId,
        fieldInfo: {
          id: fieldId,
          name: variable.name,
          type: variable.type,
        },
      };
    }
  }
  return null;
};

/**
 * Serializa un campo con posible conexión a variable
 */
const serializeField = (
  fieldName: string,
  value: string | number | null,
  type: "STRING" | "FLOAT",
  isVariable: boolean,
  variableId: string | undefined,
  variables?: NodeVariable[],
) => {
  const field: {
    name: string;
    value: string | number | null;
    type: "STRING" | "FLOAT";
    connection?: string;
    field?: string;
    source_connections?: Array<{
      field: {
        id: string;
        name: string;
        type: string;
      };
      source_node_id: string;
    }>;
  } = {
    name: fieldName,
    value: isVariable ? null : value,
    type,
  };

  if (isVariable && variableId) {
    const connection = getVariableConnection(variableId, variables);
    if (connection) {
      if (connection.fieldId && connection.fieldInfo) {
        field.source_connections = [
          {
            field: connection.fieldInfo,
            source_node_id: connection.nodeId,
          },
        ];
        field.field = connection.fieldId; // El backend guarda y devuelve esto
      }
      field.connection = connection.nodeId;
    }
  }

  return field;
};

/**
 * Serializa los datos del nodo de payment al formato del backend
 */
export const serializePaymentNode = (
  nodeData: NodeData,
  formData: PaymentData,
  variables?: NodeVariable[],
): Partial<ApiNode> => {
  const inputs: Input = [
    {
      name: "title",
      value: formData.title,
      type: "STRING",
    },
    {
      name: "description",
      value: formData.description || "",
      type: "STRING",
    },
    {
      name: "is_mandatory",
      value: formData.paymentRequired,
      type: "BOOLEAN",
    },
    {
      name: "is_express",
      value: formData.express,
      type: "BOOLEAN",
    },
  ];

  formData.paymentItems.forEach((item, index) => {
    if (item.conceptType === "input") {
      inputs.push(
        serializeField(
          `concepts_${index}`,
          item.concept,
          "STRING",
          false,
          undefined,
          variables,
        ),
      );
    }
    if (
      item.conceptType === "variable" &&
      item.conceptNode &&
      (item.conceptOutput || item.conceptField)
    ) {
      inputs.push({
        name: `concepts_${index}`,
        value: null,
        type: "STRING",
        connection: item.conceptNode,
        output_name: item.conceptOutput,
        field: item.conceptField,
      });
    }
    if (
      item.conceptType === "variable" &&
      item.conceptNode &&
      !item.conceptField &&
      !item.conceptOutput
    ) {
      inputs.push({
        name: `concepts_${index}`,
        value: null,
        type: "STRING",
        connection: item.conceptNode,
      });
    }

    if (item.conceptType === "variable" && item.conceptGlobalType) {
      inputs.push({
        name: `concepts_${index}`,
        value: item.conceptGlobalType.value,
        type: "STRING",
        global_type: item.conceptGlobalType.type,
      });
    }

    if (item.amountType === "input") {
      inputs.push({
        name: `values_${index}`,
        value: parseFloat(item.amount) || 0,
        type: "FLOAT",
      });
    }
    if (
      item.amountType === "variable" &&
      item.amountNode &&
      item.amountOutput
    ) {
      inputs.push({
        name: `values_${index}`,
        value: null,
        type: "FLOAT",
        connection: item.amountNode,
        output_name: item.amountOutput,
      });
    }
    if (item.amountType === "variable" && item.amountNode && item.amountField) {
      inputs.push({
        name: `values_${index}`,
        value: null,
        type: "FLOAT",
        connection: item.amountNode,
        field: item.amountField,
      });
    }
    if (
      item.amountType === "variable" &&
      item.amountNode &&
      !item.amountField &&
      !item.amountOutput
    ) {
      inputs.push({
        name: `values_${index}`,
        value: null,
        type: "FLOAT",
        connection: item.amountNode,
      });
    }

    if (item.amountType === "variable" && item.amountGlobalType) {
      inputs.push({
        name: `values_${index}`,
        value: item.amountGlobalType.value,
        type: "FLOAT",
        global_type: item.amountGlobalType.type,
      });
    }

    // inputs.push(
    //   serializeField(
    //     `concepts_${index}`,
    //     item.concept,
    //     "STRING",
    //     item.conceptType === "variable",
    //     item.conceptVariable,
    //     variables,
    //   ),
    // );

    // inputs.push(
    //   serializeField(
    //     `values_${index}`,
    //     item.amountType === "input" ? parseFloat(item.amount) || 0 : null,
    //     "FLOAT",
    //     item.amountType === "variable",
    //     item.amountVariable,
    //     variables,
    //   ),
    // );
  });

  inputs.push({
    name: "payment_methods",
    value: "['GATEWAY','NPE']",
    type: "STRING",
  });

  return {
    ref_name: "payment-node",
    display_name: nodeData.displayName,
    type: "PAYMENT",
    inputs,
  };
};
